import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import * as companyApi from '../../services/company-api.js';
import type { AgentThread, AgentThreadMessage } from '../../services/company-api.js';
import { wsClient } from '../../services/websocket-client.js';

@customElement('cp-company-chat')
export class CpCompanyChat extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) companyId!: number;
  @property({ type: Number }) agentId!: number;
  @property({ type: String }) companyName = '';

  @state() private _threads: AgentThread[] = [];
  @state() private _activeThread: AgentThread | null = null;
  @state() private _messages: AgentThreadMessage[] = [];
  @state() private _inputValue = '';
  @state() private _newTopicValue = '';
  @state() private _sending = false;
  @state() private _loadingThread = false;
  @state() private _showNewThread = false;
  @state() private _error = '';
  @state() private _editingMessageId: number | null = null;
  @state() private _editingContent = '';

  private _wsHandler = ((e: CustomEvent) => {
    const msg = e.detail as { type: string; payload: { threadId: number; message: AgentThreadMessage } };
    if (msg.type !== 'agent-thread:response') return;

    const { threadId, message } = msg.payload;
    if (this._activeThread?.id === threadId) {
      this._messages = [...this._messages, message];
      this._sending = false;
      this._scrollToBottom();
      // Update thread timestamp
      this._threads = this._threads.map(t =>
        t.id === threadId ? { ...t, updated_at: message.created_at } : t
      );
    }
  }) as EventListener;

  override connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler);
    void this._loadThreads();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler);
  }

  private async _loadThreads(): Promise<void> {
    if (!this.companyId) return;
    try {
      this._threads = await companyApi.listAgentThreads(this.companyId, 'founder-to-agent');
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Threads';
    }
  }

  private async _selectThread(thread: AgentThread): Promise<void> {
    if (this._loadingThread) return;
    this._loadingThread = true;
    this._activeThread = thread;
    this._messages = [];
    this._editingMessageId = null;
    this._editingContent = '';
    try {
      const [data, pendingStatus] = await Promise.all([
        companyApi.getAgentThread(thread.id),
        companyApi.getThreadPendingStatus(thread.id),
      ]);
      this._messages = data.messages;
      this._sending = pendingStatus.pending;
      this._scrollToBottom();
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden des Threads';
    } finally {
      this._loadingThread = false;
    }
  }

  private async _createThread(): Promise<void> {
    const topic = this._newTopicValue.trim();
    if (!topic || !this.agentId) return;

    try {
      const thread = await companyApi.createAgentThread({
        company_id: this.companyId,
        agent_id: this.agentId,
        topic,
      });
      this._threads = [thread, ...this._threads];
      this._newTopicValue = '';
      this._showNewThread = false;
      await this._selectThread(thread);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Erstellen des Threads';
    }
  }

  private async _sendMessage(): Promise<void> {
    const content = this._inputValue.trim();
    if (!content || !this._activeThread || this._sending) return;

    this._inputValue = '';
    this._sending = true;
    this._error = '';

    try {
      const result = await companyApi.sendAgentThreadMessage(this._activeThread.id, content);
      this._messages = [...this._messages, result.userMessage];
      this._scrollToBottom();
      // _sending stays true - will be set to false when WS response arrives
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Senden';
      this._inputValue = content;
      this._sending = false;
    }
  }

  private _scrollToBottom(): void {
    void this.updateComplete.then(() => {
      const container = this.querySelector('.cp-company-chat__messages');
      if (container) container.scrollTop = container.scrollHeight;
    });
  }

  private _onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this._sendMessage();
    }
  }

  private _onTopicKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void this._createThread();
    } else if (e.key === 'Escape') {
      this._showNewThread = false;
    }
  }

  private _formatTime(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  private _startEdit(msg: AgentThreadMessage): void {
    if (this._sending) return;
    this._editingMessageId = msg.id;
    this._editingContent = msg.content;
  }

  private _cancelEdit(): void {
    this._editingMessageId = null;
    this._editingContent = '';
  }

  private _onEditKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this._cancelEdit();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this._submitEdit();
    }
  }

  private async _submitEdit(): Promise<void> {
    const content = this._editingContent.trim();
    if (!content || !this._activeThread || !this._editingMessageId || this._sending) return;

    const messageId = this._editingMessageId;
    const threadId = this._activeThread.id;

    // Optimistic: truncate messages from edit point
    const preservedMessages = this._messages.filter(m => m.id < messageId);
    this._messages = preservedMessages;
    this._editingMessageId = null;
    this._editingContent = '';
    this._sending = true;
    this._error = '';

    try {
      const result = await companyApi.editAndResendAgentThreadMessage(threadId, messageId, content);
      this._messages = [...preservedMessages, result.userMessage];
      this._scrollToBottom();
      // _sending stays true - will be set to false when WS response arrives
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Bearbeiten';
      this._sending = false;
      // Reload thread for consistent state
      try {
        const data = await companyApi.getAgentThread(threadId);
        this._messages = data.messages;
      } catch {
        // keep error visible
      }
    }
  }

  private _renderThreadItem(thread: AgentThread) {
    const isActive = this._activeThread?.id === thread.id;
    return html`
      <li
        class="cp-company-chat__thread-item${isActive ? ' cp-company-chat__thread-item--active' : ''}"
        @click=${() => void this._selectThread(thread)}
      >
        <span class="cp-company-chat__thread-topic">${thread.topic}</span>
        <span class="cp-company-chat__thread-date">${this._formatTime(thread.updated_at)}</span>
        <button
          class="cp-company-chat__thread-delete"
          @click=${(e: Event) => void this._deleteThread(thread, e)}
          title="Thread löschen"
        >&times;</button>
      </li>
    `;
  }

  private async _deleteThread(thread: AgentThread, e: Event): Promise<void> {
    e.stopPropagation();
    if (!confirm(`Thread "${thread.topic}" wirklich löschen?`)) return;

    try {
      await companyApi.deleteAgentThread(thread.id);
      this._threads = this._threads.filter(t => t.id !== thread.id);
      if (this._activeThread?.id === thread.id) {
        this._activeThread = null;
        this._messages = [];
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Löschen';
    }
  }

  private _renderMarkdown(content: string): ReturnType<typeof html> {
    const rawHtml = marked.parse(content, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private _renderMessage(msg: AgentThreadMessage) {
    const isUser = msg.role === 'user';
    const isEditing = this._editingMessageId === msg.id;

    if (isUser && isEditing) {
      return html`
        <div class="cp-company-chat__message cp-company-chat__message--user">
          <div class="cp-company-chat__message-role">Du</div>
          <div class="cp-company-chat__message-bubble cp-company-chat__message-bubble--editing">
            <textarea
              class="cp-company-chat__edit-textarea"
              rows="3"
              .value=${this._editingContent}
              @input=${(e: Event) => { this._editingContent = (e.target as HTMLTextAreaElement).value; }}
              @keydown=${this._onEditKeydown}
            ></textarea>
            <div class="cp-company-chat__edit-actions">
              <button class="cp-company-chat__edit-cancel" @click=${this._cancelEdit}>Abbrechen</button>
              <button
                class="cp-company-chat__edit-submit"
                @click=${() => void this._submitEdit()}
                ?disabled=${!this._editingContent.trim()}
              >Senden</button>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="cp-company-chat__message cp-company-chat__message--${msg.role}">
        <div class="cp-company-chat__message-role">${isUser ? 'Du' : 'CEO'}</div>
        <div class="cp-company-chat__message-bubble">
          <div class="cp-company-chat__message-content${isUser ? '' : ' cp-company-chat__message-content--markdown'}">
            ${isUser ? msg.content : this._renderMarkdown(msg.content)}
          </div>
          <div class="cp-company-chat__message-time">
            ${this._formatTime(msg.created_at)}
            ${isUser ? html`
              <button
                class="cp-company-chat__edit-btn"
                @click=${() => this._startEdit(msg)}
                title="Nachricht bearbeiten"
              >&#9998;</button>
            ` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      <div class="cp-company-chat">
        <!-- Thread Sidebar -->
        <aside class="cp-company-chat__sidebar">
          <div class="cp-company-chat__sidebar-header">
            <span class="cp-company-chat__sidebar-title">CEO Threads</span>
            <button
              class="cp-company-chat__new-btn"
              @click=${() => { this._showNewThread = !this._showNewThread; }}
              title="Neuen Thread starten"
            >+</button>
          </div>

          ${this._showNewThread ? html`
            <div class="cp-company-chat__new-thread">
              <input
                class="cp-company-chat__topic-input"
                type="text"
                placeholder="Thema eingeben..."
                .value=${this._newTopicValue}
                @input=${(e: Event) => { this._newTopicValue = (e.target as HTMLInputElement).value; }}
                @keydown=${this._onTopicKeydown}
                autofocus
              />
              <button
                class="cp-company-chat__topic-confirm"
                @click=${() => void this._createThread()}
                ?disabled=${!this._newTopicValue.trim()}
              >Starten</button>
            </div>
          ` : nothing}

          <ul class="cp-company-chat__thread-list">
            ${this._threads.map(t => this._renderThreadItem(t))}
            ${this._threads.length === 0 && !this._showNewThread ? html`
              <li class="cp-company-chat__no-threads">Noch keine Threads. Starte ein Gespräch.</li>
            ` : nothing}
          </ul>
        </aside>

        <!-- Chat Area -->
        <main class="cp-company-chat__main">
          ${this._activeThread ? html`
            <div class="cp-company-chat__header">
              <span class="cp-company-chat__header-topic">${this._activeThread.topic}</span>
              ${this.companyName ? html`<span class="cp-company-chat__header-company">${this.companyName}</span>` : nothing}
            </div>

            <div class="cp-company-chat__messages">
              ${this._loadingThread ? html`<div class="cp-company-chat__loading">Laden...</div>` : nothing}
              ${this._messages.map(m => this._renderMessage(m))}
              ${this._sending ? html`
                <div class="cp-company-chat__message cp-company-chat__message--assistant">
                  <div class="cp-company-chat__message-role">CEO</div>
                  <div class="cp-company-chat__message-bubble">
                    <div class="cp-company-chat__typing">...</div>
                  </div>
                </div>
              ` : nothing}
            </div>

            ${this._error ? html`<div class="cp-company-chat__error">${this._error}</div>` : nothing}

            <div class="cp-company-chat__input-row">
              <textarea
                class="cp-company-chat__input"
                rows="4"
                placeholder="Nachricht an CEO..."
                .value=${this._inputValue}
                @input=${(e: Event) => { this._inputValue = (e.target as HTMLTextAreaElement).value; }}
                @keydown=${this._onKeydown}
                ?disabled=${this._sending}
              ></textarea>
              <button
                class="cp-company-chat__send-btn"
                @click=${() => void this._sendMessage()}
                ?disabled=${this._sending || !this._inputValue.trim()}
              >${this._sending ? '...' : 'Senden'}</button>
            </div>
          ` : html`
            <div class="cp-company-chat__empty">
              <p>Wähle einen Thread aus oder starte ein neues Gespräch mit dem CEO.</p>
            </div>
          `}
        </main>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-company-chat': CpCompanyChat;
  }
}
